export const OBJECTIVE_CLIPBOARD_TYPE = 'objective'
export const OBJECTIVE_CLIPBOARD_VERSION = 1

export interface ObjectiveFormState {
    name: string
    objectiveNumber: string
    domain: string
    domainAbbreviation: string
    description: string
    risk: string
    frequency: string
    type: string
    itDependency: string
    testType: string
    specification: string
    populationType: string
    failureCriteria: string
    testPlan: string
    abbreviation: string
    isKeyObjective: boolean
    requiredSystems?: string[]
}

interface ClipboardPayload {
    type: string
    version: number
    source_env?: string | null
    exported_at?: string
    objective: Record<string, unknown>
}

const normalizeSourceEnv = (sourceEnv?: string | null): string | null => {
    if (!sourceEnv) return null
    const normalized = sourceEnv.trim().toLowerCase()
    if (normalized === 'produs') return 'us'
    if (['prod', 'staging', 'test'].includes(normalized)) return 'eu'
    return normalized
}

const toTrimmedString = (value: unknown): string => {
    if (typeof value === 'string') return value.trim()
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
    return ''
}

const toBoolean = (value: unknown): boolean | null => {
    if (typeof value === 'boolean') return value
    if (typeof value === 'number') return value === 1 ? true : value === 0 ? false : null
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()
        if (['true', '1', 'yes', 'y', 'on'].includes(normalized)) return true
        if (['false', '0', 'no', 'n', 'off'].includes(normalized)) return false
    }
    return null
}

const normalizeRequiredSystems = (requiredSystems?: string[]): string[] | undefined => {
    if (!requiredSystems) return undefined
    const values = requiredSystems.map(value => value.trim()).filter(Boolean)
    return values.length > 0 ? values : undefined
}

const toStringArray = (value: unknown): string[] | undefined => {
    if (!Array.isArray(value)) return undefined
    const values = value.filter((item): item is string => typeof item === 'string').map(item => item.trim()).filter(Boolean)
    return values.length > 0 ? values : undefined
}

export const buildObjectiveClipboardPayload = (
    objective: ObjectiveFormState,
    sourceEnv?: string | null,
    requiredSystems?: string[],
) => ({
    type: OBJECTIVE_CLIPBOARD_TYPE,
    version: OBJECTIVE_CLIPBOARD_VERSION,
    source_env: normalizeSourceEnv(sourceEnv),
    exported_at: new Date().toISOString(),
    objective: {
        name: objective.name,
        objective_number: objective.objectiveNumber,
        domain: objective.domain,
        domain_abbreviation: objective.domainAbbreviation,
        description: objective.description,
        risk: objective.risk,
        frequency: objective.frequency,
        type: objective.type,
        it_dependency: objective.itDependency,
        IT_dependency: objective.itDependency,
        test_type: objective.testType,
        specification: objective.specification,
        population_type: objective.populationType,
        failure_criteria: objective.failureCriteria,
        test_plan: objective.testPlan,
        abbreviation: objective.abbreviation,
        is_key_objective: objective.isKeyObjective ?? false,
        required_systems: normalizeRequiredSystems(requiredSystems ?? objective.requiredSystems),
        order: objective.objectiveNumber,
    },
})

export const parseObjectiveClipboardPayload = (raw: string): {
    objective: ObjectiveFormState
    sourceEnv?: string | null
    isWrappedPayload: boolean
} => {
    const parsed: ClipboardPayload | Record<string, unknown> = JSON.parse(raw)
    const wrapper = parsed as ClipboardPayload

    const objectiveSource = wrapper.type === OBJECTIVE_CLIPBOARD_TYPE
        ? wrapper.objective
        : parsed

    if (!objectiveSource || typeof objectiveSource !== 'object') {
        throw new Error('Clipboard payload must be an objective JSON object')
    }

    const source = objectiveSource as Record<string, unknown>
    const objectiveNumber = toTrimmedString(source.objectiveNumber ?? source.objective_number)
    if (!objectiveNumber) {
        throw new Error('Objective Number is required')
    }

    const isKeyObjective = source.isKeyObjective ?? source.is_key_objective
    const parsedBool = toBoolean(isKeyObjective)
    if (isKeyObjective !== undefined && isKeyObjective !== null && parsedBool === null) {
        throw new Error('is_key_objective must be a boolean value')
    }

    return {
        sourceEnv: wrapper.type === OBJECTIVE_CLIPBOARD_TYPE ? wrapper.source_env ?? null : null,
        isWrappedPayload: wrapper.type === OBJECTIVE_CLIPBOARD_TYPE,
        objective: {
            name: toTrimmedString(source.name),
            objectiveNumber,
            domain: toTrimmedString(source.domain),
            domainAbbreviation: toTrimmedString(source.domainAbbreviation ?? source.domain_abbreviation),
            description: toTrimmedString(source.description),
            risk: toTrimmedString(source.risk),
            frequency: toTrimmedString(source.frequency),
            type: toTrimmedString(source.type),
            itDependency: toTrimmedString(source.itDependency ?? source.IT_dependency),
            testType: toTrimmedString(source.testType ?? source.test_type),
            specification: toTrimmedString(source.specification),
            populationType: toTrimmedString(source.populationType ?? source.population_type),
            failureCriteria: toTrimmedString(source.failureCriteria ?? source.failure_criteria),
            testPlan: toTrimmedString(source.testPlan ?? source.test_plan),
            abbreviation: toTrimmedString(source.abbreviation),
            isKeyObjective: toBoolean(source.isKeyObjective ?? source.is_key_objective) ?? false,
            requiredSystems: toStringArray(source.requiredSystems ?? source.required_systems),
        },
    }
}
